import { useEffect, useState, useRef, useMemo } from 'react';
import {
  Button, Container, Typography, Box, Stack, FormControl, InputLabel,
  Select, MenuItem, Paper, CircularProgress
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import '../../Calendar.css';
import MiniDrawer from '../../components/MiniDrawer.js';
import { useTheme } from '@mui/material/styles';
import useAlert from '../../alert/useAlert.js';
import { supabase } from '../../supabaseClient.js';
import { CLIENT_CHAT_SESSION_TYPES } from '../../utils/chatSessionsQuery.js';
import { useSelector, shallowEqual } from 'react-redux';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, isAfter, isBefore, isEqual } from 'date-fns';
import * as XLSX from 'xlsx';
import Loader from '../../loader.js';

const normalizeDigits = (value) => String(value ?? '').replace(/\D/g, '');

const decryptText = async (value) => {
  if (!value) return '';
  try {
    const { data, error } = await supabase.rpc('decrypt_text', { encrypted_data: value });
    if (!error) return data?.result ?? data ?? '';
  } catch (err) {
    console.error('decrypt_text error:', err);
  }
  return value || '';
};

const parseJsonSafe = (value, fallback = null) => {
  if (value == null) return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const parseArraySafe = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return value
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  }
};

const viewType = 'Vitals';

const DataExport = () => {
  const theme = useTheme();
  const { setAlert } = useAlert();
  const hasMounted = useRef(false);
  const { currentOrg, userDetails } = useSelector(
    (state) => ({
      currentOrg: state.globalState.currentOrg,
      userDetails: state.globalState.userDetails,
    }),
    shallowEqual
  );

  // const isCorporateVitalsMode = userDetails?.mode === 'Corporates' && viewType === 'Vitals';
  const isCorporateVitalsMode = false;

  const [dataType, setDataType] = useState('Patients');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exportFormat, setExportFormat] = useState('Excel');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [patientsData, setPatientsData] = useState([]);
  const [vitalsData, setVitalsData] = useState([]);
  const [chatsData, setChatsData] = useState([]);

  useEffect(() => {
    if (!currentOrg?.id) return;
    fetchAllData();
  }, [currentOrg?.id, isCorporateVitalsMode]);

  useEffect(() => {
    // keep valid default data type when mode changes
    if (isCorporateVitalsMode && dataType === 'Patients') {
      setDataType('Vitals');
    }
  }, [isCorporateVitalsMode, dataType]);

  const fetchAllData = async () => {
    if (!currentOrg?.id) return;
    if (isCorporateVitalsMode) {
      await fetchAllDataVitals();
    } else {
      await fetchAllDataMindvault();
    }
  };

  const fetchAllDataMindvault = async () => {
    if (!currentOrg?.id) return;

    setLoading(true);
    try {
      // 1) patients (team)
      const { data: patients, error: patientsError } = await supabase
        .from('patients')
        .select(`
          id, createdAt, teamid, name, firstName, lastName,
          phone_code, phoneNumber, gender, birth_date, age, status, join_date,
          case_owner, remarks, hkid, preExistingConditions, latestComplaint,
          insights, emergency_contact, presenting_concerns, diagnosis, emergency_contact_person
        `)
        .eq('teamid', currentOrg.id);

      if (patientsError) throw patientsError;

      const decryptedPatients = await Promise.all(
        (patients || []).map(async (p) => ({
          ...p,
          name: await decryptText(p.name),
          phone_code: await decryptText(p.phone_code),
          phoneNumber: await decryptText(p.phoneNumber),
          firstName: await decryptText(p.firstName),
          lastName: await decryptText(p.lastName),
          gender: await decryptText(p.gender),
          birth_date: await decryptText(p.birth_date),
          age: await decryptText(p.age),
          status: await decryptText(p.status),
          remarks: await decryptText(p.remarks),
          hkid: await decryptText(p.hkid),
          insights: await decryptText(p.insights),
          emergency_contact: await decryptText(p.emergency_contact),
          presenting_concerns: await decryptText(p.presenting_concerns),
          emergency_contact_person: await decryptText(p.emergency_contact_person),
        }))
      );

      // 2) mindvault accounts
      const { data: mvAccounts, error: mvErr } = await supabase
        .from('mindvault_accounts')
        .select('id, phone_code, phone_number');

      if (mvErr) throw mvErr;

      const decryptedMv = await Promise.all(
        (mvAccounts || []).map(async (a) => {
          const phone_code = await decryptText(a.phone_code);
          const phone_number = await decryptText(a.phone_number);
          return { id: a.id, phoneKey: `${normalizeDigits(phone_code)}${normalizeDigits(phone_number)}` };
        })
      );

      const accountByPhone = new Map();
      decryptedMv.forEach((a) => {
        if (a.phoneKey) accountByPhone.set(a.phoneKey, a.id);
      });

      // linked patients only
      const linkedPatients = decryptedPatients
        .map((p) => {
          const phoneKey = `${normalizeDigits(p.phone_code)}${normalizeDigits(p.phoneNumber)}`;
          const userId = accountByPhone.get(phoneKey) || null;
          return { ...p, mindvault_user_id: userId, phoneKey };
        })
        .filter((p) => !!p.mindvault_user_id);

      const linkedUserIds = linkedPatients.map((p) => p.mindvault_user_id);

      // 3) vitals
      const { data: scans, error: scanError } = await supabase
        .from('scan_history')
        .select(`
          user_id, created_at, heart_rate, hr_variability, sbp, dbp,
          oxygen_saturation, stress_index, respiration_rate, activity,
          sleep, equilibrium, metabolism, health, relaxation
        `)
        .in('user_id', linkedUserIds)
        .order('created_at', { ascending: false });

      if (scanError) throw scanError;

      // 4) chats
      const { data: chats, error: chatError } = await supabase
        .from('chat_sessions')
        .select('user_id, created_at, session_id, risk_level, risk_signals, session_summary, key_themes, emotions, title')
        .in('user_id', linkedUserIds)
        .eq('is_deleted', false)
        .or(CLIENT_CHAT_SESSION_TYPES)
        .order('created_at', { ascending: false });

      if (chatError) throw chatError;

      //decrypt chats fields
      const decryptedChats = await Promise.all(
        (chats || []).map(async (c) => ({
          ...c,
          risk_signals: JSON.parse(await decryptText(c.risk_signals || '[]')),
          key_themes: JSON.parse(await decryptText(c.key_themes || '[]')),
          emotions: JSON.parse(await decryptText(c.emotions || '[]')),
          title: await decryptText(c.title),
          session_summary: await decryptText(c.session_summary),
        }))
      );

      const sessionIds = (decryptedChats || []).map((c) => c.session_id).filter(Boolean);

      let conversationMap = {};
      if (sessionIds.length > 0) {
        const { data: conversations, error: convErr } = await supabase
          .rpc('get_decrypted_conversations', { p_session_ids: sessionIds });

        if (!convErr) {
          conversationMap = (conversations || []).reduce((acc, row) => {
            acc[row.chat_session_id] = Array.isArray(row.conversation) ? row.conversation : [];
            return acc;
          }, {});
        }
      }

      // attach patient name for vitals/chats
      const patientByUserId = new Map();
      linkedPatients.forEach((p) => {
        patientByUserId.set(p.mindvault_user_id, p);
      });

      const vitalsWithPatient = (scans || []).map((s) => {
        const p = patientByUserId.get(s.user_id);
        const phone = `${p?.phone_code || ''}${p?.phoneNumber || ''}`.trim();
        return {
          ...s,
          patientName: `${p?.lastName ?? ''} ${p?.firstName ?? ''}`.trim(),
          phone,
        };
      });

      const chatsWithPatient = (decryptedChats || []).map((c) => {
        const p = patientByUserId.get(c.user_id);
        const phone = `${p?.phone_code || ''}${p?.phoneNumber || ''}`.trim();
        const convo = conversationMap[c.session_id] || [];
        const transcript = convo
          .map((m) => `${m.role === 'assistant' ? 'AI' : 'User'}:\n${m.content || ''}`)
          .join('\n\n');

        return {
          ...c,
          patientName: `${p?.lastName ?? ''} ${p?.firstName ?? ''}`.trim(),
          phone,
          conversation_text: transcript,
        };
      });

      setPatientsData(linkedPatients);
      setVitalsData(vitalsWithPatient);
      setChatsData(chatsWithPatient);
    } catch (error) {
      console.error('Error fetching data:', error);
      setAlert('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };


  const fetchAllDataVitals = async () => {
    if (!currentOrg?.id) return;

    setLoading(true);
    try {
      const [scanRes, chatRes] = await Promise.all([
        supabase
          .from('scan_history')
          .select(`
            id, session_id, created_at, team_id, user_id,
            user_tag, gender, height, weight, birth,
            blood_pressure, heart_rate, hr_variability, sbp, dbp,
            oxygen_saturation, stress_index, respiration_rate,
            activity, sleep, equilibrium, metabolism, health, relaxation
          `)
          .eq('team_id', currentOrg.id)
          .is('user_id', null)
          .order('created_at', { ascending: false }),
        supabase
          .from('chat_sessions')
          .select(`
            id, session_id, created_at, team_id, user_id,
            title, risk_level, risk_signals, session_summary, key_themes, emotions,
            user_details
          `)
          .eq('team_id', currentOrg.id)
          .is('user_id', null)
          .eq('is_deleted', false)
          .order('created_at', { ascending: false }),
      ]);

      if (scanRes.error) throw scanRes.error;
      if (chatRes.error) throw chatRes.error;

      const scans = scanRes.data || [];
      const chatsRaw = chatRes.data || [];

      // decrypt chat fields if encrypted, fallback to plain text automatically
      const chats = await Promise.all(
        chatsRaw.map(async (c) => {
          const [titleRaw, summaryRaw, themesRaw, emotionsRaw] = await Promise.all([
            decryptText(c.title),
            decryptText(c.session_summary),
            decryptText(c.key_themes),
            decryptText(c.emotions),
          ]);

          const riskSignals = Array.isArray(c.risk_signals)
            ? c.risk_signals
            : parseArraySafe(c.risk_signals);

          return {
            ...c,
            title: titleRaw || '',
            session_summary: summaryRaw || '',
            key_themes: parseArraySafe(themesRaw),
            emotions: parseArraySafe(emotionsRaw),
            risk_signals: riskSignals,
            user_details: parseJsonSafe(c.user_details, {}),
          };
        })
      );

      // optional conversation export (same pattern as existing flow)
      const sessionIds = chats.map((c) => c.session_id).filter(Boolean);
      let conversationMap = {};
      if (sessionIds.length > 0) {
        const { data: conversations, error: convErr } = await supabase
          .rpc('get_decrypted_conversations', { p_session_ids: sessionIds });

        if (!convErr) {
          conversationMap = (conversations || []).reduce((acc, row) => {
            acc[row.chat_session_id] = Array.isArray(row.conversation) ? row.conversation : [];
            return acc;
          }, {});
        }
      }

      // latest-by-session for fallback profile enrichment
      const latestScanBySession = new Map();
      scans.forEach((s) => {
        if (!s.session_id) return;
        const prev = latestScanBySession.get(s.session_id);
        if (!prev || new Date(s.created_at) > new Date(prev.created_at)) {
          latestScanBySession.set(s.session_id, s);
        }
      });

      const latestChatBySession = new Map();
      chats.forEach((c) => {
        if (!c.session_id) return;
        const prev = latestChatBySession.get(c.session_id);
        if (!prev || new Date(c.created_at) > new Date(prev.created_at)) {
          latestChatBySession.set(c.session_id, c);
        }
      });

      const vitalsWithParticipant = scans.map((s) => {
        const chat = s.session_id ? latestChatBySession.get(s.session_id) : null;
        const ud = parseJsonSafe(chat?.user_details, {});
        return {
          ...s,
          participantId: ud?.userID || s.user_tag || '',
          patientName: ud?.userID || s.user_tag || '',
          sex: ud?.sex || s.gender || '',
          height_profile: ud?.height ?? s.height ?? '',
          weight_profile: ud?.weight ?? s.weight ?? '',
          birthYear: ud?.birthYear ?? s.birth ?? '',
          phone: '',
        };
      });

      const chatsWithParticipant = chats.map((c) => {
        const scan = c.session_id ? latestScanBySession.get(c.session_id) : null;
        const ud = parseJsonSafe(c.user_details, {});
        const convo = conversationMap[c.session_id] || [];
        const transcript = convo
          .map((m) => `${m.role === 'assistant' ? 'AI' : 'User'}:\n${m.content || ''}`)
          .join('\n\n');

        return {
          ...c,
          participantId: ud?.userID || scan?.user_tag || '',
          patientName: ud?.userID || scan?.user_tag || '',
          sex: ud?.sex || scan?.gender || '',
          height_profile: ud?.height ?? scan?.height ?? '',
          weight_profile: ud?.weight ?? scan?.weight ?? '',
          birthYear: ud?.birthYear ?? scan?.birth ?? '',
          phone: '',
          conversation_text: transcript,
        };
      });

      setPatientsData([]); // no patients table in corporate-vitals flow
      setVitalsData(vitalsWithParticipant);
      setChatsData(chatsWithParticipant);
    } catch (error) {
      console.error('Error fetching corporate vitals export data:', error);
      setAlert('Failed to fetch corporate vitals export data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const inRange = (date, start, end) => {
    if (!start && !end) return true;
    if (!date) return false;
    const d = new Date(date);
    if (start && end) {
      return (
        (isAfter(d, start) || isEqual(d, start)) &&
        (isBefore(d, end) || isEqual(d, end))
      );
    }
    if (start) return isAfter(d, start) || isEqual(d, start);
    if (end) return isBefore(d, end) || isEqual(d, end);
    return true;
  };

  const getFilteredPatients = () => {
    return patientsData.filter((p) => inRange(p.createdAt || p.created_at, startDate, endDate));
  };

  const getFilteredVitals = () => {
    return vitalsData.filter((v) => inRange(v.created_at, startDate, endDate));
  };

  const getFilteredChats = () => {
    return chatsData.filter((c) => inRange(c.created_at, startDate, endDate));
  };

  const formatPatientForExport = (p) => ({
    'Patient Name': `${p.lastName ?? ''} ${p.firstName ?? ''}`.trim(),
    'Full Name': p.name || '',
    'Gender': p.gender || '',
    'Birth Date': p.birth_date || '',
    'Age': p.age || '',
    'Status': p.status || '',
    'Phone Code': p.phone_code || '',
    'Phone Number': p.phoneNumber || '',
    'Case Owner': Array.isArray(p.case_owner) ? p.case_owner.join(', ') : (p.case_owner || ''),
    'Remarks': p.remarks || '',
    'HKID': p.hkid || '',
    'Pre-Existing Conditions': Array.isArray(p.preExistingConditions)
      ? p.preExistingConditions.join(', ')
      : (p.preExistingConditions || ''),
    'Latest Complaint': p.latestComplaint || '',
    'Insights': p.insights || '',
    'Emergency Contact': p.emergency_contact || '',
    'Emergency Contact Person': p.emergency_contact_person || '',
    'Presenting Concerns': p.presenting_concerns || '',
    'Diagnosis': Array.isArray(p.diagnosis) ? p.diagnosis.join(', ') : (p.diagnosis || ''),
    'Created At': p.createdAt ? format(new Date(p.createdAt), 'PPpp') : '',
  });

  const formatVitalForExport = (v) => ({
    ...(isCorporateVitalsMode
      ? {
          'Participant ID': v.participantId || '',
          'Sex': v.sex || '',
          'Height': v.height_profile ?? '',
          'Weight': v.weight_profile ?? '',
          'Birth Year': v.birthYear ?? '',
          'Session ID': v.session_id || '',
        }
      : {
          'Patient Name': v.patientName || '',
          'Phone Number': v.phone || '',
        }),
    'Scan Date': v.created_at ? format(new Date(v.created_at), 'PPpp') : '',
    'Heart Rate': v.heart_rate ?? '',
    'HRV': v.hr_variability ?? '',
    'SBP': v.sbp ?? '',
    'DBP': v.dbp ?? '',
    'Oxygen Saturation': v.oxygen_saturation ?? '',
    'Stress Index': v.stress_index ?? '',
    'Respiration Rate': v.respiration_rate ?? '',
    'Activity': v.activity ?? '',
    'Sleep': v.sleep ?? '',
    'Equilibrium': v.equilibrium ?? '',
    'Metabolism': v.metabolism ?? '',
    'Health': v.health ?? '',
    'Relaxation': v.relaxation ?? '',
  });

  const formatChatForExport = (c) => ({
    ...(isCorporateVitalsMode
      ? {
          'Participant ID': c.participantId || '',
          'Sex': c.sex || '',
          'Height': c.height_profile ?? '',
          'Weight': c.weight_profile ?? '',
          'Birth Year': c.birthYear ?? '',
          'Session ID': c.session_id || '',
        }
      : {
          'Patient Name': c.patientName || '',
          'Phone Number': c.phone || '',
        }),
    'Session Date': c.created_at ? format(new Date(c.created_at), 'PPpp') : '',
    'Title': c.title || '',
    'Risk Level': c.risk_level || '',
    'Risk Signals': Array.isArray(c.risk_signals) ? c.risk_signals.join(', ') : (c.risk_signals || ''),
    'Key Themes': Array.isArray(c.key_themes) ? c.key_themes.join(', ') : (c.key_themes || ''),
    'Emotions': Array.isArray(c.emotions) ? c.emotions.join(', ') : (c.emotions || ''),
    'Summary': c.session_summary || '',
    'Conversation': c.conversation_text || '',
  });

  const handleExport = async () => {
    try {
      setExporting(true);
      if (startDate) setStartDate(new Date(startDate.setHours(0, 0, 0, 0)));
      if (endDate) setEndDate(new Date(endDate.setHours(23, 59, 59, 999)));

      const workbook = XLSX.utils.book_new();

      if (dataType === 'All') {
        const vitalsFiltered = getFilteredVitals();
        const chatsFiltered = getFilteredChats();

        if (!isCorporateVitalsMode) {
          const patientsFiltered = getFilteredPatients();
          XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.json_to_sheet(patientsFiltered.map(formatPatientForExport)),
            'Patients'
          );
        }

        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(vitalsFiltered.map(formatVitalForExport)),
          'Vitals'
        );
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(chatsFiltered.map(formatChatForExport)),
          'Chats'
        );
      } else if (dataType === 'Patients') {
        if (isCorporateVitalsMode) {
          setAlert('Patients export is not available in Corporate Vitals mode', 'warning');
          return;
        }
        const patientsFiltered = getFilteredPatients();
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(patientsFiltered.map(formatPatientForExport)),
          'Patients'
        );
      } else if (dataType === 'Vitals') {
        const vitalsFiltered = getFilteredVitals();
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(vitalsFiltered.map(formatVitalForExport)),
          'Vitals'
        );
      } else if (dataType === 'Chats') {
        const chatsFiltered = getFilteredChats();
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(chatsFiltered.map(formatChatForExport)),
          'Chats'
        );
      }

      const exportFileName = `${dataType}_Export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      XLSX.writeFile(workbook, exportFileName);
      setAlert('Data exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      setAlert('Failed to export data', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleResetDates = () => {
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <Container disableGutters maxWidth={false}>
      <Grid container sx={{overflow: 'auto', minHeight: '100vh', minWidth: '100vw'}}>
        <Grid size={{xs: 1, md: 0}}>
          <MiniDrawer />
        </Grid>
        <Grid size="grow">
          <Grid container spacing={5} sx={{ backgroundColor: '#F8F9FA', display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
            <Grid size={12}>
              <div style={{ padding: '24px 10vw' }}>
                <Typography variant="h4" sx={{ mb: 4 }}>Export Data</Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <Loader />
                  </Box>
                ) : (
                  <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
                    <Stack spacing={3}>
                      <FormControl fullWidth>
                        <InputLabel id="data-type-label">Data Type</InputLabel>
                        <Select
                          labelId="data-type-label"
                          value={dataType}
                          label="Data Type"
                          onChange={(e) => setDataType(e.target.value)}
                        >
                          {!isCorporateVitalsMode && <MenuItem value="Patients">Patients</MenuItem>}
                          <MenuItem value="Vitals">Vitals</MenuItem>
                          <MenuItem value="Chats">Chats</MenuItem>
                          <MenuItem value="All">All</MenuItem>
                        </Select>
                      </FormControl>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={setStartDate}
                            sx={{ flexGrow: 1 }}
                            slotProps={{ textField: { fullWidth: true } }}
                          />
                          <Typography sx={{ mx: 1 }}>to</Typography>
                          <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={setEndDate}
                            sx={{ flexGrow: 1 }}
                            slotProps={{ textField: { fullWidth: true } }}
                          />
                          <Button
                            variant="outlined"
                            onClick={handleResetDates}
                            sx={{
                              borderRadius: '50px',
                              backgroundColor: 'white',
                              color: '#29C0BB',
                              height: '56px',
                              border: 'none'
                            }}
                          >
                            Reset
                          </Button>
                        </LocalizationProvider>
                      </Box>

                      <FormControl fullWidth>
                        <InputLabel id="format-label">Format</InputLabel>
                        <Select
                          labelId="format-label"
                          value={exportFormat}
                          label="Format"
                          onChange={(e) => setExportFormat(e.target.value)}
                        >
                          <MenuItem value="Excel">Excel (.xlsx)</MenuItem>
                        </Select>
                      </FormControl>

                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={handleExport}
                          disabled={exporting}
                          sx={{
                            minWidth: 200,
                            backgroundColor: '#29C0BB',
                            borderRadius: '30px',
                            '&:hover': { backgroundColor: '#1e8e8a' },
                          }}
                        >
                          {exporting ? (
                            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                          ) : 'Export'}
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>
                )}
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DataExport;